import { useParamDrivenValue, useRouter } from '@mi-front/core';
import { alpha, Box, Chip, useTheme } from '@mui/material';
import React from 'react';

import type { ChipProps } from '@mui/material/Chip';

export type ChipItem = {
  value: string;
  label: React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactElement;
  color?: ChipProps['color'];
};

export type ChipNavigationProps = Partial<ChipProps> & {
  chips: ChipItem[];
  defaultActiveChip?: string;
  header?: React.ReactNode;
  param?: string;
  /**
   * When set, navigation uses the URL path instead of query params.
   * Current value is read from the path segment after pathBase (or from pathParamName if set).
   * Chip clicks navigate to `${pathBase}/${chipValue}`.
   */
  pathBase?: string;
  /**
   * When using pathBase, name of the route path param that holds the active chip (e.g. "category" for path "/audit/matches/:category").
   * If not set, the active chip is derived from the pathname by stripping pathBase and taking the first segment.
   */
  pathParamName?: string;
  /**
   * When using pathBase, use history.replace instead of history.push when a chip is clicked.
   */
  pathReplace?: boolean;

  Wrapper?: React.ComponentType<{ children?: React.ReactNode }>;

  ContentWrapper?: React.ComponentType<{ children?: React.ReactNode }>;
};

const DEFAULT_PARAM = 'tab';

/**
 * Derives the current path segment after pathBase from location.pathname.
 * pathBase is normalized (no trailing slash). Returns the first segment after pathBase or empty string.
 */
function getPathSegment(pathname: string, pathBase: string): string {
  const base = pathBase.replace(/\/$/, '') || '';
  if (!base || pathname === base) return '';
  const rest = pathname.startsWith(base + '/') ? pathname.slice(base.length + 1) : '';
  const segment = rest.split('/')[0] ?? '';
  return segment;
}

export function ChipNavigationMui(p: ChipNavigationProps) {
  const {
    chips,
    size = 'medium',
    sx,
    defaultActiveChip,
    header,
    param,
    pathBase,
    pathParamName,
    pathReplace = false,
    Wrapper = Box,
    ContentWrapper = Box,
    ...rest
  } = p;

  const validChipValues = React.useMemo(() => chips.map((c) => c.value), [chips]);

  const queryParam = useParamDrivenValue({
    param: param || DEFAULT_PARAM,
    defaultValue: defaultActiveChip,
    validValues: validChipValues,
  });

  const router = useRouter();
  const { history, location, match } = router;

  const isPathMode = pathBase !== undefined && pathBase !== '';

  const pathValue = React.useMemo(() => {
    if (!isPathMode) return null;
    const fromParam =
      pathParamName && match?.params
        ? (match.params as Record<string, string>)[pathParamName]
        : undefined;
    const raw = fromParam ?? getPathSegment(location.pathname, pathBase);
    const valid = validChipValues.includes(raw) ? raw : null;
    return valid ?? defaultActiveChip ?? validChipValues[0] ?? '';
  }, [
    isPathMode,
    pathParamName,
    match?.params,
    location.pathname,
    pathBase,
    validChipValues,
    defaultActiveChip,
  ]);

  const theme = useTheme();

  const activeValue = isPathMode ? pathValue : queryParam.value;

  const handleChipClick = (chipValue: string) => {
    if (!validChipValues.includes(chipValue)) return;
    if (isPathMode) {
      const base = pathBase!.replace(/\/$/, '') || '';
      const targetPath = `${base}/${chipValue}`;
      if (pathReplace) {
        history.replace(targetPath);
      } else {
        history.push(targetPath);
      }
    } else {
      queryParam.setValue(chipValue);
    }
  };

  const activeChipContent = chips.find((chip) => chip.value === activeValue)?.content;

  return (
    <Box sx={{ width: '100%', ...sx }} {...rest}>
      <Wrapper>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {chips.map((chip) => {
              const isActive = activeValue === chip.value;
              const chipColor = chip.color ?? 'primary';

              return (
                <Chip
                  key={chip.value}
                  icon={chip.icon}
                  label={chip.label}
                  color={isActive ? chipColor : 'default'}
                  variant={'outlined'}
                  size={size}
                  onClick={() => handleChipClick(chip.value)}
                  sx={{
                    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : undefined,
                    fontWeight: isActive ? 500 : 400,
                  }}
                />
              );
            })}
          </Box>
          {header && <Box>{header}</Box>}
        </Box>
      </Wrapper>
      {activeChipContent && <ContentWrapper>{activeChipContent}</ContentWrapper>}
    </Box>
  );
}
